import { formatISO } from 'date-fns'
import Vue, { nextTick, watch } from 'vue'

import constants from '@/constants'
import getI18n from '@/logic/i18nInstance'
import { useOnlineUserSessionStore } from '@/modules/account/onlineUserSessionStore'
import chatApi from '@/modules/chat/api/chat'
import { useAzureChatStore } from '@/modules/chat/azureChatStore'
import { useChatAttachmentsStore } from '@/modules/chat/chatAttachmentsStore'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import store from '@/store'

let wasDisconnected = false
let listenersLoaded = false

/**
 * Returns true if chat is enabled in the app settings.
 * Returns false if config is not loaded or settings.module_chat is falsy.
 */
export function isChatEnabled() {
  const config = store.getters['auth/config']
  if (!config) {
    return false
  }
  if (!store.getters['auth/isLoggedIn']) {
    return false
  }
  return !!config.settings?.module_chat
}

export async function initializeChatThreads() {
  if (!isChatEnabled()) {
    return
  }
  const chatStore = useChatStore()
  await chatStore.fetchMyChatThreads()
  await chatStore.fetchLastMessages()
  await chatStore.updateHasUnreadMessagesInThreads()
  if (chatStore.colleagues.length === 0) {
    await chatStore.fetchRandomUsers()
  }
}

function getNotificationBody(content, metadata) {
  const fileType = metadata?.file_type ? parseInt(metadata.file_type) : undefined
  if (fileType === constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE) {
    return getI18n().t('notifications.chat_sent_image')
  }
  if (fileType === constants.LEARNINGMATERIALS.TYPE_VIDEO) {
    return getI18n().t('notifications.chat_sent_video')
  }
  if (fileType !== undefined) {
    return getI18n().t('notifications.chat_sent_file')
  }

  // Remove HTML tags from content
  const cleanContent = content ? content.replace(/<[^>]*>/g, '') : ''

  // For text messages, truncate to ~100 characters without cutting words
  if (cleanContent && cleanContent.length > 100) {
    const truncated = cleanContent.substring(0, 100)
    const lastSpace = truncated.lastIndexOf(' ')
    return lastSpace > 80 ? `${truncated.substring(0, lastSpace)}...` : `${truncated}...`
  }
  return cleanContent || ''
}

export async function listenToChatEvents() {
  if (!isChatEnabled()) {
    return
  }
  if (listenersLoaded) {
    return
  }

  const azureChatStore = useAzureChatStore()
  const chatClient = await azureChatStore.getChatClient()
  await chatClient.startRealtimeNotifications()
  const chatStore = useChatStore()
  const onlineUserSessionStore = useOnlineUserSessionStore()

  function findParticipant(userId) {
    const foundThread = chatStore.myChatThreads.find((thread) => thread.participant && thread.participant.user_id === parseInt(userId))
    return foundThread?.participant
  }

  // Warn users if they attempt to close the page while a chat file upload is in progress
  const beforeUnloadHandler = (e) => {
    const threadId = chatStore.openAzureChatThreadId
    if (!threadId) {
      return
    }
    const chatAttachmentsStore = useChatAttachmentsStore(threadId)

    if (chatAttachmentsStore && chatAttachmentsStore.isUploading) {
      e.preventDefault()
      // Setting returnValue triggers the browser's default confirmation dialog
      e.returnValue = ''
    }
  }
  window.addEventListener('beforeunload', beforeUnloadHandler)

  watch(
    // Setting the read receipt for an open chat thread after coming online again because we don't send receipts when the user isn't online
    () => onlineUserSessionStore.isOnline,
    async (isOnline) => {
      if (isOnline && chatStore.openAzureChatThreadId) {
        const chatThreadStore = useChatThreadStore(chatStore.openAzureChatThreadId)

        if (chatThreadStore.isScrollerAtBottom) {
          const newestMessage = chatThreadStore.sortedMessages[chatThreadStore.sortedMessages.length - 1]
          if (newestMessage) {
            await chatThreadStore.setReadReceipt(newestMessage.id.toString())
            // When the user is online and has opened chat thread, we need to update the hasUnreadMessages flag
            chatStore.updateHasUnreadMessagesInThreads(chatThreadStore.azureChatThreadId)
          }
        }
      }
    },
  )

  const pendingThreadFetches = new Map()

  async function getThreadByUserId(userId) {
    // Prevent concurrent fetches for the same user - return existing promise if in-flight
    if (pendingThreadFetches.has(userId)) {
      return pendingThreadFetches.get(userId)
    }

    const fetchPromise = (async () => {
      const thread = await chatApi.getChatThread(userId)
      const participant = thread.participant
      participant.threadId = thread.id
      participant.azure_chat_thread_id = thread.azure_chat_thread_id
      participant.lastMessage = {}
      thread.participant = participant

      // Check if thread already exists before adding (safety check)
      const existingThread = chatStore.myChatThreads.find((t) => t.azure_chat_thread_id === thread.azure_chat_thread_id)
      if (existingThread) {
        return existingThread
      }

      chatStore.myChatThreads.push(thread)
      return thread
    })()

    pendingThreadFetches.set(userId, fetchPromise)
    try {
      return await fetchPromise
    } finally {
      pendingThreadFetches.delete(userId)
    }
  }

  chatClient.on('chatMessageReceived', async (newMessage) => {
    const chatThreadExist = !!chatStore.myChatThreads.find((chatThread) => chatThread.azure_chat_thread_id === newMessage.threadId)
    let thread = chatStore.myChatThreads.find((t) => t.azure_chat_thread_id === newMessage.threadId)
    // We need this check, to be able to add the chatThread to our list if we didn't talk with this user before
    if (!thread) {
      thread = await getThreadByUserId(newMessage.senderDisplayName)
    }
    const chatThreadStore = useChatThreadStore(newMessage.threadId)
    let messageContent = newMessage.message
    if (chatThreadStore.tempFilesMessages[newMessage.id]) {
      if (newMessage.metadata.file_type === constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE || newMessage.metadata.file_type === constants.LEARNINGMATERIALS.TYPE_AUDIO) {
        messageContent = chatThreadStore.tempFilesMessages[newMessage.id]
      }
      delete chatThreadStore.tempFilesMessages[newMessage.id]
    }

    function pushOrUpdateMessage(messageIndex = -1) {
      if (messageIndex !== -1) {
        chatThreadStore.messages[messageIndex] = {
          id: parseInt(newMessage.id),
          content: messageContent,
          senderDisplayName: newMessage.senderDisplayName,
          createdOn: newMessage.createdOn,
          metadata: newMessage.metadata,
        }
      } else {
        chatThreadStore.messages.push({
          id: parseInt(newMessage.id),
          content: messageContent,
          senderDisplayName: newMessage.senderDisplayName,
          createdOn: newMessage.createdOn,
          metadata: newMessage.metadata,
        })
      }
    }

    // We check if the message was sent by the user, but didn't get a messageId from Azure
    // So we store it immediately in our pinia store, and once we got a message from azure,
    // we can compare it with existing ones, If it's there so we can replace it instead of adding it again to messages list
    if (newMessage.metadata?.messageId) {
      const messageIdx = chatThreadStore.messages.findIndex((message) => message.metadata?.messageId && +message.metadata.messageId === +newMessage.metadata.messageId)

      pushOrUpdateMessage(messageIdx)
    } else {
      pushOrUpdateMessage()
    }

    chatThreadStore.typingIndicator = undefined
    const isScrollerAtBottom = chatThreadStore.isScrollerAtBottom
    chatThreadStore.updateSortedMessages()
    chatStore.setChatThreadLastMessage(newMessage.threadId)

    const chatThreadIndex = chatStore.myChatThreads.findIndex((myChatThread) => myChatThread.azure_chat_thread_id === newMessage.threadId)

    if (chatThreadIndex !== -1) {
      chatStore.myChatThreads[chatThreadIndex].updated_at = formatISO(newMessage.createdOn)
    }

    if (isScrollerAtBottom) {
      await chatThreadStore.scrollContainerDown()
    } else {
      chatThreadStore.unSeenMessages++
    }

    chatStore.updateHasUnreadMessagesInThreads(newMessage.threadId)
    if (chatStore.openAzureChatThreadId === newMessage.threadId && isScrollerAtBottom) {
      // It sets a reading receipt when a user receives a new message for an open chat thread
      chatThreadStore.setReadReceipt(newMessage.id)
      chatStore.markThreadAsRead(newMessage.threadId)
    }

    if (onlineUserSessionStore.isOnline && !chatStore.isMe(newMessage.senderDisplayName) && chatStore.openAzureChatThreadId !== newMessage.threadId) {
      const threadOfNewMessage = chatStore.myChatThreads.find((t) => t.azure_chat_thread_id === newMessage.threadId)
      const participant = findParticipant(newMessage.senderDisplayName)

      store.commit('notifications/addNotification', {
        topic: participant?.displayName,
        title: getNotificationBody(messageContent, newMessage.metadata),
        route: {
          name: 'chat.open-chat-thread',
          params: { chatThreadId: threadOfNewMessage.id },
        },
        avatar: participant?.avatar_url,
      })
    }
    if (!chatThreadExist && thread) {
      thread.participant.lastMessage = await chatThreadStore.getLastMessage()
    }
    // Resort messages to update the thread order when getting a new message
    await nextTick()
    chatStore.resortThreads = true
  })

  chatClient.on('chatMessageEdited', async (message) => {
    const chatThreadStore = useChatThreadStore(message.threadId)
    const index = chatThreadStore.messages.findIndex((threadMessage) => +threadMessage.id === +message.id)
    if (index !== -1) {
      chatThreadStore.messages[index] = {
        ...chatThreadStore.messages[index],
        content: message.message,
        metadata: message.metadata,
      }
      await chatThreadStore.updateSortedMessages()
    }
    // Update replied-to cache if this message is cached
    const messageIdStr = message.id.toString()
    if (chatThreadStore.repliedToMessages[messageIdStr]) {
      Vue.set(chatThreadStore.repliedToMessages, messageIdStr, {
        ...chatThreadStore.repliedToMessages[messageIdStr],
        content: message.message,
        metadata: message.metadata,
      })
    }
  })

  chatClient.on('chatMessageDeleted', async (message) => {
    const chatThreadStore = useChatThreadStore(message.threadId)
    chatThreadStore.messages = chatThreadStore.messages.filter((threadMessage) => +threadMessage.id !== +message.id)
    chatThreadStore.allLoadedMessages = chatThreadStore.allLoadedMessages.filter((threadMessage) => +threadMessage.id !== +message.id)
    await chatThreadStore.updateSortedMessages()
    await chatStore.setChatThreadLastMessage(message.threadId)
    await chatStore.updateHasUnreadMessagesInThreads(message.threadId)
    // Mark as deleted in replied-to cache
    const messageIdStr = message.id.toString()
    if (chatThreadStore.repliedToMessages[messageIdStr]) {
      Vue.set(chatThreadStore.repliedToMessages, messageIdStr, { deleted: true })
    }
  })

  chatClient.on('readReceiptReceived', (newReadReceipt) => {
    const chatThreadStore = useChatThreadStore(newReadReceipt.threadId)
    chatThreadStore.partnerReadReceipt = {
      chatMessageId: newReadReceipt.chatMessageId,
      readOn: newReadReceipt.readOn,
      sender: { ...newReadReceipt.sender },
    }
  })

  chatClient.on('typingIndicatorReceived', (typingEvent) => {
    const chatThreadStore = useChatThreadStore(typingEvent.threadId)
    if (typingEvent.sender.communicationUserId === typingEvent.recipient.communicationUserId) {
      return
    }

    // For some reason setting the value directly didn't work, and typingIndicator is not reactive,
    // So we use Vue.set to force set the value in the store
    Vue.set(chatThreadStore, 'typingIndicator', {
      senderDisplayName: typingEvent.senderDisplayName,
      receivedOn: typingEvent.receivedOn,
    })

    clearTimeout(chatThreadStore.typingIndicatorTimeout)
    chatThreadStore.typingIndicatorTimeout = setTimeout(() => {
      chatThreadStore.typingIndicator = null
    }, 2000)
  })

  chatClient.on('realTimeNotificationDisconnected', async () => {
    wasDisconnected = true
  })

  chatClient.on('realTimeNotificationConnected', async () => {
    if (wasDisconnected) {
      await initializeChatThreads()
      const openThreadId = chatStore.openAzureChatThreadId
      if (openThreadId) {
        const chatThreadStore = useChatThreadStore(openThreadId)
        await chatThreadStore.reset()

        await chatThreadStore.initializeMessageIterator()
        await chatThreadStore.loadMoreMessages()
        try {
          await chatThreadStore.loadReadReceipts(true)
        } catch (error) {
          console.error('Failed to load read receipts:', error)
        }

        await chatStore.setChatThreadLastMessage(openThreadId)
        await chatStore.updateHasUnreadMessagesInThreads(openThreadId)

        chatThreadStore.chatThreadIsOpening = false
      }
      wasDisconnected = false
    }
  })

  listenersLoaded = true
}
